// apps/web/src/features/russia-requests/components/editor/RussiaEditorPositionsCard.tsx
//
// SIMPLE positions block for the Russia editor (#1545).
// Mounts the shared `DraftPhotoPreviews` provider with a Russia-scoped
// download-URL resolver, then renders one `RussiaPositionEditRow` per
// local row. Sync against the persisted DRAFT id lives in
// `useRussiaDraftPositions`.
//
// @aiSecurity the registry's signed download URLs are bearer-like and
//   stay in in-memory refs only (provider guarantee); this card passes a
//   Russia-pathed resolver but adds no new persistence of those URLs.

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { russiaRequestItemPhotoDownloadUrl } from '../../../../api/russia-requests/storage.js';
import { DraftPhotoPreviewsProvider } from '../../../requests/draft-media/index.js';
import { Card } from './EditorPrimitives.js';
import { RussiaAddPositionButton, RussiaPositionEditRow } from './RussiaPositionEditRow.js';
import {
  useRussiaDraftPositions,
  type UseRussiaDraftPositionsOptions,
} from './useRussiaDraftPositions.js';

export interface RussiaEditorPositionsCardProps {
  persistedId: string | null;
  ensurePersisted: UseRussiaDraftPositionsOptions['ensurePersisted'];
  /** Already-persisted rows to seed in edit mode (#1571). */
  initialRows?: UseRussiaDraftPositionsOptions['initialRows'];
  onError?: ((message: string) => void) | undefined;
  /** Hands the host this card's position-flush fn so Save can persist
   *   create-valid local rows before navigating (#1545 F-003). */
  registerFlush?: ((flush: (parentId: string) => Promise<void>) => void) | undefined;
  /** Hands the host this card's never-saved-photo-key collector so a
   *   confirmed discard-close can GC the orphaned upload objects (#1561). */
  registerCollectNeverSavedPhotoKeys?: ((collect: () => Promise<string[]>) => void) | undefined;
  /** Reports whether positions hold unsaved work so the host can fold it
   *   into the drawer's unsaved-changes guard (#1545 round-6 COD-REV-001). */
  onDirtyChange?: ((dirty: boolean) => void) | undefined;
}

export function RussiaEditorPositionsCard({
  persistedId,
  ensurePersisted,
  initialRows,
  onError,
  registerFlush,
  registerCollectNeverSavedPhotoKeys,
  onDirtyChange,
}: RussiaEditorPositionsCardProps) {
  const { t } = useTranslation('russiaRequests');
  const positions = useRussiaDraftPositions({ persistedId, ensurePersisted, onError, initialRows });

  useEffect(() => {
    registerFlush?.(positions.flushPending);
  }, [registerFlush, positions.flushPending]);

  useEffect(() => {
    registerCollectNeverSavedPhotoKeys?.(positions.collectNeverSavedPhotoKeys);
  }, [registerCollectNeverSavedPhotoKeys, positions.collectNeverSavedPhotoKeys]);

  useEffect(() => {
    onDirtyChange?.(positions.hasUnsavedWork);
  }, [onDirtyChange, positions.hasUnsavedWork]);

  return (
    <DraftPhotoPreviewsProvider fetchDownloadUrl={russiaRequestItemPhotoDownloadUrl}>
      <Card title={t('editor.positions.title')}>
        {positions.rows.length === 0 ? (
          <p style={{ fontSize: 13, color: 'var(--fg-3)', margin: '0 0 12px' }}>
            {t('editor.positions.empty')}
          </p>
        ) : (
          positions.rows.map((row, index) => (
            <RussiaPositionEditRow
              key={row.localId}
              row={row}
              index={index}
              persistedId={persistedId}
              onPatch={(patch) => positions.patchPosition(row.localId, patch)}
              onRemove={() => positions.removePosition(row.localId)}
              onAddPhoto={(file) => positions.addPhoto(row.localId, file)}
              onRemovePhoto={(key) => positions.removePhoto(row.localId, key)}
            />
          ))
        )}
        <RussiaAddPositionButton onClick={positions.addPosition} />
      </Card>
    </DraftPhotoPreviewsProvider>
  );
}
